import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, ShieldCheck, Target, Mic, Users, FileSearch, Shield } from "lucide-react";

const ICONS = {
  "graduation-cap": GraduationCap,
  "shield-check": ShieldCheck,
  target: Target,
  mic: Mic,
  users: Users,
  "file-search": FileSearch,
  shield: Shield,
};

export default function Services() {
  const { services } = useContent();

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="services-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">[ 03 · services ]</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">
        Human-centric cybersecurity, delivered as service lines.
      </h1>
      <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
        Each engagement is scoped around what your people actually need to do differently. Frameworks are the how — behavior is the what.
      </p>

      <div className="grid gap-4 md:grid-cols-12 mt-16">
        {services.map((s, i) => {
          const Icon = ICONS[s.icon] || Shield;
          const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className={`group relative p-8 lg:p-10 rounded-2xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300 ${spans[i % spans.length]}`}
              data-testid={`service-detail-${i}`}
            >
              <div className="flex items-center justify-between mb-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ 0{i + 1} ]
                </div>
                <Icon className="w-6 h-6 text-accent" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl tracking-tight">{s.title}</h2>
              <div className="font-mono text-xs uppercase tracking-wider text-accent mt-2 mb-6">
                {s.tagline}
              </div>
              <p className="text-muted-foreground leading-relaxed">{s.description}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-24 p-10 lg:p-16 rounded-3xl border border-border bg-secondary/40">
        <div className="max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">[ next step ]</div>
          <h3 className="font-serif text-3xl sm:text-4xl tracking-tight">Have a scoping conversation.</h3>
          <p className="mt-4 text-muted-foreground">
            I offer a 30-minute discovery call to understand your risk landscape and the humans in it. No pitch — just a conversation.
          </p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em]">
            Get in touch <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
